import { createTheme } from '@mui/material/styles';
import type { Theme, ThemeOptions, CSSObject } from '@mui/material/styles';
import type { ButtonProps } from '@mui/material/Button';
import type { TextFieldProps } from '@mui/material/TextField';

// Extend the Theme interface to include custom properties
declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      drawer: {
        width: number;
        collapsedWidth: number;
      };
    };
  }
  
  // Allow configuration using `createTheme`
  interface ThemeOptions {
    custom?: {
      drawer?: {
        width?: number;
        collapsedWidth?: number;
      };
    };
  }
}

// Shared theme configuration
const getDesignTokens = (mode: 'light' | 'dark') => {
  const isLight = mode === 'light';
  
  return {
    palette: {
      mode,
      ...(isLight
        ? {
            // Light mode
            primary: {
              main: '#1976d2',
              light: '#42a5f5',
              dark: '#1565c0',
              contrastText: '#fff',
            },
            secondary: {
              main: '#9c27b0',
              light: '#ba68c8',
              dark: '#7b1fa2',
              contrastText: '#fff',
            },
            error: {
              main: '#d32f2f',
              light: '#ef5350',
              dark: '#c62828',
            },
            warning: {
              main: '#ed6c02',
              light: '#ff9800',
              dark: '#e65100',
            },
            info: {
              main: '#0288d1',
              light: '#03a9f4',
              dark: '#01579b',
            },
            success: {
              main: '#2e7d32',
              light: '#4caf50',
              dark: '#1b5e20',
            },
            background: {
              default: '#fff',
              paper: '#fff',
            },
            text: {
              primary: 'rgba(0, 0, 0, 0.87)',
              secondary: 'rgba(0, 0, 0, 0.6)',
              disabled: 'rgba(0, 0, 0, 0.38)',
            },
          }
        : {
            // Dark mode
            primary: {
              main: '#90caf9',
              light: '#e3f2fd',
              dark: '#42a5f5',
              contrastText: 'rgba(0, 0, 0, 0.87)',
            },
            secondary: {
              main: '#ce93d8',
              light: '#f3e5f5',
              dark: '#ab47bc',
              contrastText: 'rgba(0, 0, 0, 0.87)',
            },
            error: {
              main: '#f44336',
              light: '#e57373',
              dark: '#d32f2f',
            },
            warning: {
              main: '#ffa726',
              light: '#ffb74d',
              dark: '#f57c00',
            },
            info: {
              main: '#29b6f6',
              light: '#4fc3f7',
              dark: '#0288d1',
            },
            success: {
              main: '#66bb6a',
              light: '#81c784',
              dark: '#388e3c',
            },
            background: {
              default: '#121212',
              paper: '#1e1e1e',
            },
            text: {
              primary: '#fff',
              secondary: 'rgba(255, 255, 255, 0.7)',
              disabled: 'rgba(255, 255, 255, 0.5)',
            },
          }),
    },
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
      h1: {
        fontWeight: 600,
        fontSize: '2.5rem',
        lineHeight: 1.2,
      },
      h2: {
        fontWeight: 600,
        fontSize: '2rem',
        lineHeight: 1.2,
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.75rem',
        lineHeight: 1.2,
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.2,
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.2,
      },
      h6: {
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: 1.2,
      },
    },
    custom: {
      drawer: {
        width: 260,
        collapsedWidth: 72,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: (theme) => ({
          body: {
            backgroundColor: isLight ? '#fff' : theme.palette.grey[900],
            transition: 'background-color 0.3s ease',
          },
        }),
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
          variant: 'contained' as const,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            padding: '8px 16px',
          } as CSSObject,
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
            '&:hover': {
              boxShadow: '0 8px 30px 0 rgba(0,0,0,0.1)',
            },
            transition: 'box-shadow 0.3s ease',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined' as const,
          fullWidth: true,
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
    },
  };
};

// Create a theme instance
export const createAppTheme = (mode: 'light' | 'dark' = 'light') => {
  return createTheme(getDesignTokens(mode));
};

// Create light and dark theme instances
export const lightTheme = createAppTheme('light');
export const darkTheme = createAppTheme('dark');

// Default export for backward compatibility
export default lightTheme;

